import { forwardRef } from "react";
import type { ForwardedRef, ReactElement } from "react";
import { twMerge } from "tailwind-merge";
import { ButtonSpinner } from "./ButtonSpinner";

type ButtonProps = {
  loading?: boolean;
  border?: boolean;
  active?: boolean;
};

export const DataButton = forwardRef(
  (
    props: React.ButtonHTMLAttributes<HTMLButtonElement> & ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ): ReactElement => {
    const { active, border, loading, ...buttonProps } = props;

    return (
      <button
        type="button"
        {...buttonProps}
        disabled={props.disabled || loading}
        ref={ref}
        className={twMerge(
          "w-fit px-3 py-2 text-sm font-medium",

          active
            ? "bg-zinc-200 hover:bg-zinc-300 dark:bg-zinc-700 dark:text-white dark:hover:bg-zinc-600"
            : "bg-white hover:bg-zinc-100 dark:bg-zinc-900/50 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-white",

          border &&
            (active
              ? "border border-solid border-zinc-400 dark:border-zinc-600"
              : "border border-solid border-zinc-300 dark:border-zinc-800"),
          border && "shadow-[0_2px_2px_0_rgba(0,0,0,0.15)] dark:shadow-none",

          props.disabled || loading
            ? "cursor-not-allowed opacity-50"
            : "cursor-pointer",

          loading && "text-transparent [&>*:not(:first-child)]:invisible",

          "relative flex items-center justify-center gap-2 rounded-lg text-center leading-5 font-semibold tracking-wide whitespace-nowrap not-italic transition-colors first-of-type:ml-0 last-of-type:mr-0 motion-reduce:transition-none",

          props.className,
        )}
      >
        {loading && (
          <div className={"absolute"}>
            <ButtonSpinner className="text-black dark:text-white" />
          </div>
        )}
        {props.children}
      </button>
    );
  },
);

DataButton.displayName = "DataButton";
